import { ActionTypes } from "../constants/action-types";
import { createAction } from "redux-actions";
import {
  generateContents,
  scheduleBatch,
  saveAIContentSetting,
  searchSocialPosts,
  SearchSocialPostsParams,
  AISettings,
} from "../api/AIContentApi";
import { GenerateContentsRequest } from "../ai-content/types/AIContentTypes";
import { getItem, searchFiles } from "../api/ProjectApi";
import { getDesigns } from "../api/CanvaApi";

const api = {
  [ActionTypes.generateContents]: async (params: GenerateContentsRequest) => {
    try {
      const data = await generateContents(
        params.projectId,
        params.topic,
        params.prompt,
        params.start_date,
        params.end_date,
        params.max_days,
        params.include_weekends,
        params.language
      );
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  [ActionTypes.saveAIContentSetting]: async (projectId: string, settings: AISettings) => {
    try {
      const data = await saveAIContentSetting(projectId, settings);
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error: error.message };
    }
  },

  [ActionTypes.updateAIContentSetting]: (settings: AISettings) => ({
    settings,
  }),

  [ActionTypes.updateSocialPosts]: (postDetail) => ({ postDetail }),
  [ActionTypes.updatePostDetail]: (postDetail) => ({ postDetail }),
  [ActionTypes.setSocialPosts]: (posts: any[]) => ({ posts }),
  [ActionTypes.showPostDetailDialog]: (postId) => getItem(postId),
  [ActionTypes.hidePostDetailDialog]: () => ({}),
  [ActionTypes.updateSharePost]: (sharePost) => ({ sharePost }),
  [ActionTypes.showSharePostDialog]: (sharePost) => ({ sharePost }),
  [ActionTypes.hideSharePostDialog]: () => ({}),
  [ActionTypes.getCanvaDesigns]: async () => {
    try {
      const data = await getDesigns();
      return { data, error: null };
    } catch (error: any) {
      return {
        data: { items: [] },
        error: error.message || "Failed to load designs",
      };
    }
  },
  [ActionTypes.getFileImages]: (projectId) =>
    searchFiles({
      projectId,
      filters: { fileContentType: { operator: "regex", values: ["image/"] } },
      sorts: [{ field: "event.createdAt", direction: "DESC" }],
    }),

  // TopicsModal actions
  [ActionTypes.openTopicsModal]: () => ({}),
  [ActionTypes.closeTopicsModal]: () => ({}),
  [ActionTypes.selectTopic]: (topicId: string) => ({ topicId }),
  [ActionTypes.deselectTopic]: (topicId: string) => ({ topicId }),
  [ActionTypes.selectAllTopics]: (topicIds: string[]) => ({ topicIds }),
  [ActionTypes.deselectAllTopics]: () => ({}),
  [ActionTypes.confirmTopicSelection]: (selectedTopic: any) => ({
    selectedTopic,
  }),
  [ActionTypes.clearSocialPosts]: () => ({}),
  [ActionTypes.setPlatformFilter]: (platform: string) => ({ platform }),
  [ActionTypes.setStatusFilter]: (status: string) => ({ status }),
  [ActionTypes.setTimezoneFilter]: (timezone: string) => ({ timezone }),
  [ActionTypes.setTimezone]: (timezone: string) => ({ timezone }),
  [ActionTypes.resetAIContentState]: () => ({}),

  [ActionTypes.clearAiSettingsChangedSinceLastGenerate]: () => ({}),

  [ActionTypes.scheduleBatch]: async (
    workspaceId: string,
    payload: {
      postIds: string[];
      publishTime: string;
      requireApproval: boolean;
    }
  ) => {
    try {
      const data = await scheduleBatch(workspaceId, payload);
      return { data, error: null };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || "Failed to schedule batch posts",
      };
    }
  },

  [ActionTypes.searchSocialPosts]: async (params: SearchSocialPostsParams) => {
    try {
      const data = await searchSocialPosts(params);
      return { data, error: null };
    } catch (error: any) {
      return {
        data: null,
        error: error.message || "Failed to search social posts",
      };
    }
  },

  [ActionTypes.refreshCalendarData]: (dateRange: { start: Date; end: Date }) => ({ dateRange }),

  [ActionTypes.setCurrentDate]: (currentDate: Date) => ({ currentDate }),
};

const actions: any = { ...api };

Object.keys(actions).forEach((type) => {
  actions[type] = createAction(type, actions[type] as any);
});

export default actions;
